import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Input, Button, Space } from 'antd';
import {
    InfoCircleOutlined,
    MedicineBoxOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import './ToothChart.css';
import { TOOTH_ACTIONS, toothNumbers } from './helpers';

const initialValues = Object.fromEntries(toothNumbers.map(num => [num, '0']));

const TOOTH_ACTION_MAP = TOOTH_ACTIONS.reduce((map, item) => {
    map[item.value] = item;
    return map;
}, {});

const getToothSrcPublic = (num) => `/tooth_images/${num}.png`;

const parseActions = (item) => {
    if (Array.isArray(item.actions)) return item.actions;

    if (item.actions_json) {
        try {
            const parsed = JSON.parse(item.actions_json);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }

    if (item.status && item.status !== 'none') {
        return [item.status];
    }

    return [];
};

const ToothChart = ({ onDataChange, data }) => {
    const [values, setValues] = useState(initialValues);
    const [selected, setSelected] = useState([]);
    const [toothActions, setToothActions] = useState(
        Object.fromEntries(toothNumbers.map(n => [n, []]))
    );
    const [deletedTeeth, setDeletedTeeth] = useState([]);

    const isFirstLoad = useRef(true);

    useEffect(() => {
        if (data && Array.isArray(data)) {
            const newValues = { ...initialValues };
            const newActions = Object.fromEntries(toothNumbers.map(n => [n, []]));
            const newDeleted = [];

            data.forEach(item => {
                const num = parseInt(item.tooth_number, 10);

                if (Number.isInteger(num) && toothNumbers.includes(num)) {
                    newValues[num] = item.tooth_size || '0';
                    newActions[num] = parseActions(item);

                    if (item.is_deleted) {
                        newDeleted.push(num);
                    }
                }
            });

            setValues(newValues);
            setToothActions(newActions);
            setDeletedTeeth(newDeleted);
            isFirstLoad.current = true;
        }
    }, [data]);

    useEffect(() => {
        if (isFirstLoad.current) {
            isFirstLoad.current = false;
            return;
        }

        if (onDataChange) {
            onDataChange(prepareDataForSave());
        }
    }, [values, toothActions, deletedTeeth]);

    const prepareDataForSave = () => {
        return toothNumbers.map(num => ({
            tooth_number: num,
            tooth_size: values[num] || '0',
            actions: toothActions[num] || [],
            actions_json: JSON.stringify(toothActions[num] || []),
            status: toothActions[num]?.[0] || 'none',
            is_deleted: deletedTeeth.includes(num) ? 1 : 0,
        }));
    };

    const selectedSummary = useMemo(() => {
        if (selected.length === 0) return 'Chưa chọn răng';
        if (selected.length === 1) return `Đang chọn răng ${selected[0]}`;
        return `Đang chọn ${selected.length} răng`;
    }, [selected]);

    const handleChange = (num, val) => {
        setValues(prev => ({
            ...prev,
            [num]: val,
        }));
    };

    const toggleSelect = (num) => {
        setSelected(prev =>
            prev.includes(num)
                ? prev.filter(n => n !== num)
                : [...prev, num]
        );
    };

    const applyAction = (actionValue) => {
        if (selected.length === 0) return;

        setToothActions(prev => {
            const next = { ...prev };

            selected.forEach(num => {
                const current = next[num] || [];

                next[num] = current.includes(actionValue)
                    ? current.filter(item => item !== actionValue)
                    : [...current, actionValue];
            });

            return next;
        });
    };

    const handleAction = (action) => {
        switch (action) {
            case 'delete':
                if (selected.length === 0) return;
                setDeletedTeeth(prev => Array.from(new Set([...prev, ...selected])));
                setSelected([]);
                break;

            case 'reset':
                if (selected.length === 0) return;

                setValues(prev => {
                    const next = { ...prev };
                    selected.forEach(num => {
                        next[num] = '0';
                    });
                    return next;
                });

                setToothActions(prev => {
                    const next = { ...prev };
                    selected.forEach(num => {
                        next[num] = [];
                    });
                    return next;
                });

                setDeletedTeeth(prev => prev.filter(num => !selected.includes(num)));
                setSelected([]);
                break;

            case 'selectAll':
                setSelected(toothNumbers);
                break;

            case 'deselect':
                setSelected([]);
                break;

            default:
                applyAction(action);
                break;
        }
    };

    const renderTooth = (num) => {
        const actions = toothActions[num] || [];
        const isShapeHidden = deletedTeeth.includes(num);
        const imgSrc = getToothSrcPublic(num);
        const hasActions = actions.length > 0;

        return (
            <div
                key={num}
                className={`main-tooth ${selected.includes(num) ? 'selected' : ''} ${isShapeHidden ? 'main-tooth-deleted' : ''}`}
                onClick={() => toggleSelect(num)}
            >
                <div className="main-tooth-number-wrapper">
                    <div className="main-tooth-number">{num}</div>
                </div>

                <Input
                    value={values[num]}
                    onChange={e => handleChange(num, e.target.value)}
                    onClick={e => e.stopPropagation()}
                    className="tooth-input main-tooth-input"
                />

                <div className="main-tooth-action-zone">
                    {hasActions ? (
                        <div className="main-tooth-action-badges">
                            {actions.map(actionValue => {
                                const action = TOOTH_ACTION_MAP[actionValue];

                                return (
                                    <div
                                        key={actionValue}
                                        className="main-tooth-action-badge"
                                        style={{
                                            background: action?.color || '#64748b',
                                        }}
                                    >
                                        {action?.label || actionValue}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="main-tooth-action-empty" />
                    )}
                </div>

                <div className="main-tooth-image-box">
                    {isShapeHidden ? (
                        <div className="main-tooth-image-placeholder" />
                    ) : (
                        <img
                            src={imgSrc}
                            alt={`Tooth ${num}`}
                            className="main-tooth-image"
                            draggable={false}
                        />
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="main-tooth-chart-wrapper">
            <div className="main-tooth-medical-header">
                <div className="main-tooth-title-block">
                    <div className="main-tooth-title-icon">
                        <MedicineBoxOutlined />
                    </div>

                    <div>
                        <div className="main-tooth-medical-title">Sơ đồ răng</div>
                        <div className="main-tooth-medical-desc">
                            Chọn một hoặc nhiều răng để gán tình trạng, chỉ định điều trị và ghi chú lâm sàng.
                        </div>
                    </div>
                </div>

                <div className="main-tooth-selected-card">
                    <div className="main-selected-label">Đang chọn</div>
                    <div className="main-selected-value">
                        {selected.length}
                        <span> răng</span>
                    </div>
                    <div className="main-selected-list">
                        {selected.length > 0 ? selected.join(', ') : selectedSummary}
                    </div>
                </div>
            </div>

            <div className="main-tooth-grid">
                <div className="main-tooth-row main-top-row">
                    {toothNumbers.slice(0, 16).map(num => renderTooth(num))}
                </div>

                <div className="main-tooth-row main-bottom-row">
                    {toothNumbers.slice(16).map(num => renderTooth(num))}
                </div>
            </div>

            <div className="main-tooth-medical-panel">
                <div className="main-tooth-toolbar-section main-quick-section">
                    <div className="main-toolbar-title">
                        <ThunderboltOutlined /> Thao tác nhanh
                    </div>

                    <Space wrap>
                        <Button onClick={() => handleAction('selectAll')}>
                            Chọn tất cả
                        </Button>

                        <Button onClick={() => handleAction('deselect')}>
                            Bỏ chọn
                        </Button>

                        <Button danger onClick={() => handleAction('delete')}>
                            Đánh dấu mất răng
                        </Button>

                        <Button onClick={() => handleAction('reset')}>
                            Reset răng
                        </Button>
                    </Space>
                </div>

                <div className="main-tooth-toolbar-section main-action-section">
                    <div className="main-toolbar-title">
                        <MedicineBoxOutlined /> Tình trạng / chỉ định điều trị
                    </div>

                    <Space wrap>
                        {TOOTH_ACTIONS.map(action => (
                            <Button
                                key={action.value}
                                className="main-tooth-action-button"
                                style={{
                                    background: action.color,
                                    borderColor: action.color,
                                }}
                                onClick={() => applyAction(action.value)}
                            >
                                {action.label}
                            </Button>
                        ))}
                    </Space>
                </div>

                <div className="main-tooth-help-box">
                    <InfoCircleOutlined />
                    <span>
                        Nhấn vào răng để chọn hoặc bỏ chọn.
                        Sau đó chọn tình trạng bên dưới để gán hoặc hủy cho các răng đã chọn.
                        Có thể reset từng răng hoặc reset toàn bộ về trạng thái ban đầu.
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ToothChart;